import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './App.css';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';


function App() {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [displayedPropertiesWithImages, setDisplayedPropertiesWithImages] = useState([]);
  const [displayedPropertiesWithoutImages, setDisplayedPropertiesWithoutImages] = useState([]);
  const [hasMoreWithImages, setHasMoreWithImages] = useState(true);
  const [hasMoreWithoutImages, setHasMoreWithoutImages] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [filters, setFilters] = useState({
    type: 'All',
    bedrooms: 'Any',
    floor: 'Any Floor',
    minPrice: '',
    maxPrice: '',
    minArea: 0,
    maxArea: 1000,
  });
  const [uniqueBedrooms, setUniqueBedrooms] = useState([]);
  const [uniqueFloors, setUniqueFloors] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  useEffect(() => {
    axios.get('https://bhagirathiestate.com/api/get_properties.php')
      .then(response => {
        if (response.data.status === 'success') {
          let data = response.data.data;
          // Filter out invalid square_yards
          data = data.filter(p => p.square_yards < 10000);
          
          // Sort by creation date (newest first)
          data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          
          setProperties(data);
          setFilteredProperties(data);

          // Extract unique bedrooms and floors
          const bedrooms = [...new Set(data.map(p => p.bhk).filter(b => b !== null).sort())];
          setUniqueBedrooms(['Any', ...bedrooms]);

          const floors = [...new Set(data.map(p => p.floor).filter(f => f !== null).sort())];
          setUniqueFloors(['Any Floor', ...floors]);
        }
      })
      .catch(error => console.error('Error fetching data:', error));
  }, []);
const navigate = useNavigate();
  const applyFilters = useCallback(() => {
    const filtered = properties.filter(p => {
      if (filters.type !== 'All' && p.type !== filters.type) return false;
      if (filters.bedrooms !== 'Any' && p.bhk !== filters.bedrooms) return false;
      if (filters.floor !== 'Any Floor' && p.floor !== filters.floor) return false;

      const priceNum = parseFloat(p.price) || 0;
      const minPriceNum = parseFloat(filters.minPrice) || 0;
      const maxPriceNum = parseFloat(filters.maxPrice) || Infinity;
      if (priceNum < minPriceNum || priceNum > maxPriceNum) return false;

      const areaNum = p.square_yards || 0;
      if (areaNum < filters.minArea || areaNum > filters.maxArea) return false;

      return true;
    });

    setFilteredProperties(filtered);
    
    // Show properties with 3 or more images first (latest first)
    const withImages = filtered.filter(p => p.images && p.images.length >= 3)
                             .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    // Then show properties without images (latest first)
    const withoutImages = filtered.filter(p => !p.images || p.images.length === 0)
                                 .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // Set initial displayed properties
    setDisplayedPropertiesWithImages(withImages.slice(0, 8));
    setDisplayedPropertiesWithoutImages(withoutImages.slice(0, 6));
    
    // Set hasMore flags
    setHasMoreWithImages(withImages.length > 8);
    setHasMoreWithoutImages(withoutImages.length > 6);
    
  }, [filters, properties]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const loadMoreWithImages = () => {
    const withImages = filteredProperties.filter(p => p.images && p.images.length >= 3)
                                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    const nextProperties = withImages.slice(
      displayedPropertiesWithImages.length, 
      displayedPropertiesWithImages.length + 4
    );
    
    setDisplayedPropertiesWithImages(prev => [...prev, ...nextProperties]);
    setHasMoreWithImages(displayedPropertiesWithImages.length + nextProperties.length < withImages.length);
  };

  const loadMoreWithoutImages = () => {
    const withoutImages = filteredProperties.filter(p => !p.images || p.images.length === 0)
                                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    const nextProperties = withoutImages.slice(
      displayedPropertiesWithoutImages.length, 
      displayedPropertiesWithoutImages.length + 4
    );
    
    setDisplayedPropertiesWithoutImages(prev => [...prev, ...nextProperties]);
    setHasMoreWithoutImages(displayedPropertiesWithoutImages.length + nextProperties.length < withoutImages.length);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleMinAreaChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setFilters(prev => ({ 
      ...prev, 
      minArea: value, 
      maxArea: Math.max(value, prev.maxArea) 
    }));
  };

  const handleMaxAreaChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setFilters(prev => ({ 
      ...prev, 
      maxArea: value, 
      minArea: Math.min(value, prev.minArea) 
    }));
  };

  const isNewProperty = (createdAt) => {
    const createdDate = new Date(createdAt);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - createdDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays <= 3;
  };

  const formatPrice = (p) => {
    if (p.type === 'Buy') {
      return `₹${p.price} CR`;
    } else {
      return `₹${p.price} Lakh`;
    }
  };

  const formatTitle = (p) => {
    return `${p.bhk || 'N/A'} BHK ${p.type} in ${p.area}`;
  };
 const handleHomeClick = () => {
    navigate('/Home');
  };
  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Navbar */}
    <nav className="sticky top-0 z-50 flex justify-between items-center px-6 py-4 bg-gray-900/80 backdrop-blur-md border-b border-gray-800">
        <div className="flex items-center">
          <img src="https://bhagirathiestate.com/assets/images/logo.png" alt="Logo" className="h-10 mr-3 rounded transition-transform duration-300 hover:scale-105" />
          <span className="text-xl font-bold text-white">Bhagirathi Estate</span>
        </div>
        
        <div className="hidden md:flex space-x-6 text-gray-300">
          <button 
            onClick={handleHomeClick}
            className="hover:text-blue-400 transition-colors duration-300 bg-transparent border-none cursor-pointer"
          >
            Home
          </button>
          <a href="https://bhagirathiestate.com/About.php" className="hover:text-blue-400 transition-colors duration-300">About Us</a>
          {/* <a href="#property" className="hover:text-blue-400 transition-colors duration-300">Property</a> */}
          <a href="https://bhagirathiestate.com/blog.php" className="hover:text-blue-400 transition-colors duration-300">Blogs</a>
          <div className="relative group">
            <button className="hover:text-blue-400 transition-colors duration-300 flex items-center bg-transparent border-none cursor-pointer">
              Tools <span className="ml-1 transition-transform duration-300 group-hover:rotate-180">▼</span>
            </button>
            <div className="absolute hidden group-hover:block bg-gray-800/90 backdrop-blur-sm shadow-lg rounded mt-1 p-2 w-40">
              <a href="https://bhagirathiestate.com/emi-calculator.php" className="block px-4 py-2 hover:bg-gray-700 rounded transition-colors duration-300">EMI Calculator</a>
              <a href="https://bhagirathiestate.com/rates-trends.php" className="block px-4 py-2 hover:bg-gray-700 rounded transition-colors duration-300">Rates & Trends</a>
              <a href="https://bhagirathiestate.com/area-convertor.php" className="block px-4 py-2 hover:bg-gray-700 rounded transition-colors duration-300">Area Convertor</a>
            
            </div>
          </div>
          <a href="https://bhagirathiestate.com/contact.php" className="hover:text-blue-400 transition-colors duration-300">Contact</a>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Filter Button - Visible on both desktop and mobile */}
          <button 
            className="text-gray-300 transition-transform duration-300 hover:scale-105 hover:text-blue-400"
            onClick={() => setIsFilterVisible(!isFilterVisible)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>

          {/* Mobile Menu Button - Only visible on mobile */}
          <button 
            className="md:hidden text-gray-300 transition-transform duration-300 hover:scale-105"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-6 py-4">
          <div className="flex flex-col space-y-4 text-gray-300">
            <button 
              onClick={handleHomeClick}
              className="hover:text-blue-400 transition-colors duration-300 text-left bg-transparent border-none cursor-pointer"
            >
              Home
            </button>
            <a href="https://bhagirathiestate.com/About.php" className="hover:text-blue-400 transition-colors duration-300">About Us</a>
            <a href="https://bhagirathiestate.com/blog.php" className="hover:text-blue-400 transition-colors duration-300">Blogs</a>
            
            {/* Mobile Tools Section */}
            <div className="border-l-2 border-gray-700 pl-4">
              <p className="text-gray-400 text-sm font-semibold mb-2">Tools</p>
              <div className="flex flex-col space-y-2">
                <a href="https://bhagirathiestate.com/emi-calculator.php" className="hover:text-blue-400 transition-colors duration-300 text-sm">EMI Calculator</a>
                <a href="https://bhagirathiestate.com/rates-trends.php" className="hover:text-blue-400 transition-colors duration-300 text-sm">Rate & Trends</a>
                
                <a href="https://bhagirathiestate.com/area-convertor.php" className="hover:text-blue-400 transition-colors duration-300 text-sm">Area Convertor</a>
              </div>
            </div>
            
            <a href="https://bhagirathiestate.com/contact.php" className="hover:text-blue-400 transition-colors duration-300">Contact</a>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`px-6 py-5 bg-gray-800/90 backdrop-blur-sm rounded-lg mx-4 my-6 transition-all duration-500 ${isFilterVisible ? 'block' : 'hidden md:block'}`}>
        <h2 className="text-xl font-semibold text-white mb-4">Find Your Perfect Property</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Buy/Rent</label>
            <select 
              name="type" 
              value={filters.type} 
              onChange={handleFilterChange}
              className="w-full bg-gray-700/70 backdrop-blur-sm border border-gray-600 p-2 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
            >
              <option>All</option>
              <option>Buy</option>
              <option>Rent</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Bedrooms</label>
            <select 
              name="bedrooms" 
              value={filters.bedrooms} 
              onChange={handleFilterChange}
              className="w-full bg-gray-700/70 backdrop-blur-sm border border-gray-600 p-2 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
            >
              {uniqueBedrooms.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Floor</label>
            <select 
              name="floor" 
              value={filters.floor} 
              onChange={handleFilterChange}
              className="w-full bg-gray-700/70 backdrop-blur-sm border border-gray-600 p-2 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
            >
              {uniqueFloors.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Price Range (₹)</label>
            <div className="flex gap-2">
              <input 
                type="number" 
                name="minPrice" 
                value={filters.minPrice} 
                onChange={handleFilterChange}
                placeholder="Min" 
                className="w-1/2 bg-gray-700/70 backdrop-blur-sm border border-gray-600 p-2 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              />
              <input 
                type="number" 
                name="maxPrice" 
                value={filters.maxPrice} 
                onChange={handleFilterChange}
                placeholder="Max" 
                className="w-1/2 bg-gray-700/70 backdrop-blur-sm border border-gray-600 p-2 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Area: {filters.minArea} - {filters.maxArea} Sq Yards
            </label>
            <div className="relative pt-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>0</span>
                <span>500</span>
                <span>1000</span>
              </div>
              <div className="relative h-2 bg-gray-700 rounded">
                <div
                  className="absolute h-2 bg-blue-500 rounded transition-all duration-300"
                  style={{
                    left: `${(filters.minArea / 1000) * 100}%`,
                    width: `${((filters.maxArea - filters.minArea) / 1000) * 100}%`,
                  }}
                ></div>
              </div>
              <input
                type="range"
                min="0"
                max="1000"
                value={filters.minArea}
                onChange={handleMinAreaChange}
                className="absolute w-full h-2 opacity-0 top-0 cursor-pointer"
              />
              <input
                type="range"
                min="0"
                max="1000"
                value={filters.maxArea}
                onChange={handleMaxAreaChange}
                className="absolute w-full h-2 opacity-0 top-0 cursor-pointer"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <span className="text-sm text-gray-400 mr-3">
            {filteredProperties.length} properties found
          </span>
          <button 
            onClick={() => setFilters({
              type: 'All',
              bedrooms: 'Any',
              floor: 'Any Floor',
              minPrice: '',
              maxPrice: '',
              minArea: 0,
              maxArea: 1000,
            })}
            className="mr-2 px-4 py-2 border border-gray-600 rounded text-gray-300 hover:bg-gray-700 transition-all duration-300"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* Properties with 3 or more images (Top Section) */}
        {displayedPropertiesWithImages.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Premium Properties</h2>
              <span className="text-sm text-gray-400">
                {displayedPropertiesWithImages.length} of {filteredProperties.filter(p => p.images && p.images.length >= 3).length} shown
              </span>
            </div>
            
            <InfiniteScroll
              dataLength={displayedPropertiesWithImages.length}
              next={loadMoreWithImages}
              hasMore={hasMoreWithImages}
              loader={<div className="text-center py-4"><div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div></div>}
              endMessage={<div className="text-center text-gray-500 py-4">All premium properties loaded</div>}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {displayedPropertiesWithImages.map(property => (
                  <div key={property.id} className="bg-gray-800/80 backdrop-blur-sm rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
                   <div className="relative">
  {property.images && property.images.length > 0 ? (
    property.images.length > 1 ? (
      <Slider 
        dots={true} 
        infinite={true} 
        arrows={true} 
        slidesToShow={1} 
        slidesToScroll={1}
        adaptiveHeight={true}
      >
        {property.images.map((img, index) => (
          <div key={index}>
            <img 
              src={img} 
              alt={`Property ${property.id}`} 
              loading="lazy" 
              className="w-full h-56 object-cover transition-transform duration-300 hover:scale-105" 
            />
          </div>
        ))}
      </Slider>
    ) : (
      <img 
        src={property.images[0]} 
        alt={`Property ${property.id}`} 
        loading="lazy" 
        className="w-full h-56 object-cover transition-transform duration-300 hover:scale-105" 
      />
    )
  ) : (
    <img 
      src="https://bhagirathiestate.com/assets/images/property.jpeg" 
      alt="Default property image" 
      loading="lazy" 
      className="w-full h-56 object-cover transition-transform duration-300 hover:scale-105" 
    />
  )}
  
  <div className="absolute top-3 left-3">
    <span className="bg-green-600/90 backdrop-blur-sm text-white px-3 py-1 rounded text-xs font-bold">
      {property.type === 'Buy' ? 'FOR SALE' : 'FOR RENT'}
    </span>
  </div>
  
  {isNewProperty(property.created_at) && (
    <span className="absolute top-3 right-3 bg-blue-600/90 backdrop-blur-sm text-white px-3 py-1 rounded text-xs font-bold animate-pulse">
      NEW
    </span>
  )}
</div>
                    
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-white mb-1">{formatTitle(property)}</h3>
                      <p className="text-blue-400 font-bold text-xl mb-2">{formatPrice(property)}</p>
                      
                      <div className="flex items-center text-sm text-gray-400 mb-2">
                        <span className="flex items-center mr-4 transition-colors duration-300 hover:text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          {property.bhk || 'N/A'} BHK
                        </span>
                        <span className="flex items-center transition-colors duration-300 hover:text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                          </svg>
                          {property.square_yards} Sq Yd
                        </span>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-400 mb-4 transition-colors duration-300 hover:text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {property.floor} Floor
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          Added: {new Date(property.created_at).toLocaleDateString()}
                        </span>
                    <button 
  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-all duration-300 flex items-center transform hover:scale-105"
  onClick={() => {
    // Encode the ID in Base64
    const encodedId = btoa(property.id.toString());
    navigate(`/property/${encodedId}`);
  }}
>
  View Details
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </InfiniteScroll>
          </div>
        )}
        
        {/* Properties without Images (Bottom Section) */}
        {displayedPropertiesWithoutImages.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white border-b border-gray-700 pb-2">Basic Listings (No Images)</h3>
              <span className="text-sm text-gray-400">
                {displayedPropertiesWithoutImages.length} of {filteredProperties.filter(p => !p.images || p.images.length === 0).length} shown
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {displayedPropertiesWithoutImages.map(property => (
                <div key={property.id} className="bg-gray-800/80 backdrop-blur-sm rounded-lg overflow-hidden shadow-lg p-5 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">{formatTitle(property)}</h3>
                      <p className="text-blue-400 font-bold text-xl mb-2">{formatPrice(property)}</p>
                      
                      <div className="flex items-center text-sm text-gray-400 mb-2">
                        <span className="mr-4 transition-colors duration-300 hover:text-white">{property.bhk || 'N/A'} BHK</span>
                        <span className="transition-colors duration-300 hover:text-white">{property.square_yards} Sq Yd</span>
                      </div>
                      
                      <div className="text-sm text-gray-400 mb-2 transition-colors duration-300 hover:text-white">
                        {property.floor} Floor
                      </div>
                      
                      <div className="flex items-center">
                        {isNewProperty(property.created_at) && (
                          <span className="bg-blue-600/90 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-bold mr-2 animate-pulse">
                            NEW
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          Added: {new Date(property.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm h-10 self-center hover:bg-blue-700 transition-all duration-300 transform hover:scale-105">
                      Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {hasMoreWithoutImages && (
              <div className="text-center mt-8">
                <button 
                  onClick={loadMoreWithoutImages}
                  className="bg-gray-700/80 backdrop-blur-sm text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-all duration-300 transform hover:scale-105"
                >
                  Load More Properties
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* No Properties Message */}
        {displayedPropertiesWithImages.length === 0 && displayedPropertiesWithoutImages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">No properties found matching your criteria</div>
            <button 
              onClick={() => setFilters({
                type: 'All',
                bedrooms: 'Any',
                floor: 'Any Floor',
                minPrice: '',
                maxPrice: '',
                minArea: 0,
                maxArea: 1000,
              })}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;